import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { fetchProfileList } from '@/api/auth';
import { useAuth } from '@/store/auth';
import { WEB_ROLES, isWebRole } from '@/api/scope';
import { Button } from '@/components/ui/Button';
import type { Profile } from '@/api/types/common';

export default function ProfileSelectPage() {
  const navigate = useNavigate();
  const setProfile = useAuth((s) => s.setProfile);
  const [profiles, setProfiles] = useState<Profile[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfileList()
      .then(({ profiles }) => setProfiles(profiles.filter((p) => WEB_ROLES.has(p.role))))
      .catch((err) => toast.error(err instanceof Error ? err.message : 'Failed to load profiles'))
      .finally(() => setLoading(false));
  }, []);

  function pick(p: Profile) {
    setProfile(p);
    // Web console supports admin, manager, and trainer. Manager/trainer data is
    // scoped server-side; all three land on the same /admin shell.
    if (isWebRole(p.role)) {
      navigate('/admin', { replace: true });
    } else {
      navigate('/unsupported-role', { replace: true });
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-md">
        <h1 className="mb-6 text-center text-2xl font-bold">Choose a profile</h1>
        {loading ? (
          <p className="text-center text-grey-500">Loading…</p>
        ) : !profiles?.length ? (
          <p className="text-center text-grey-500">No profiles found.</p>
        ) : (
          <ul className="space-y-3">
            {profiles.map((p) => (
              <li key={p.id}>
                <button
                  onClick={() => pick(p)}
                  className="card flex w-full items-center justify-between text-left hover:bg-app-surface-2"
                >
                  <div>
                    <div className="font-semibold capitalize">{p.role}</div>
                    <div className="text-sm text-grey-500">
                      {p.academy_name ?? p.name ?? `Profile #${p.id}`}
                      {p.branch_name ? ` · ${p.branch_name}` : ''}
                    </div>
                  </div>
                  <span className="text-primary">›</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-6 text-center">
          <Button variant="ghost" onClick={() => navigate('/login', { replace: true })}>
            Use a different number
          </Button>
        </div>
      </div>
    </div>
  );
}
